/** Serialise a FilterNode[] tree back into the canonical query-string syntax.
 *
 * The canonical form is chosen so that `parse(serialize(tree)) === tree` for
 * every tree the system itself produces:
 *
 * - Between ALL siblings (top-level array and group children) an EXPLICIT
 *   separator is emitted — " & " for AND, " | " for OR. Bare adjacency-as-AND
 *   is something the parser accepts on INPUT but the serializer never PRODUCES;
 *   this is what stops `[!freetext-a, freetext-b]` re-serialising ambiguously
 *   (the leading `!` would otherwise swallow the next word).
 * - Groups are always wrapped in parens.
 *
 * Canonical-tree invariant: groups have >= 2 children (a 1-child group is never
 * built — the parser collapses `!(x)` onto `x`), and freetext values are
 * single-space-normalised. Given these invariants the round-trip holds.
 */
import type { FilterNode, FilterValue } from './types';

/** Characters that force quoting when present in a value. `*` is included so a
 *  literal asterisk in a value is quoted (`tour="*x"`) and never mistaken for
 *  the any/all `*` modifier that sits right after the operator sigil. */
const SPECIAL = /["&|!():=~,*]/;

/** Quote `s` (escaping `\`->`\\` and `"`->`\"`) iff it is empty, has
 *  whitespace, contains a special char, or contains "..". Otherwise return
 *  it unchanged. Exported: the widget UI needs it for chip text. */
export function quoteValue(s: string): boolean {
  return s === '' || /\s/.test(s) || SPECIAL.test(s) || s.includes('..');
}

/** Quote `s` iff `quoteValue` says it needs it. Exported for the chip bridge
 *  and the widget's paste path, which rebuild DSL text from stored values. */
export function quote(s: string): string {
  if (!quoteValue(s)) return s;
  return `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/** Inverse of `quote()` for raw user input: if `s` is fully wrapped in a
 *  matching pair of double quotes, strip them and unescape `\"`/`\\` inside
 *  (mirroring the tokenizer's own QUOTED-token parsing — see tokenizer.ts).
 *  Without this, a value the user quoted themselves while typing or pasting
 *  (e.g. `"search string"`) would be stored WITH the delimiting quotes still
 *  attached, and re-quoting it for display/serialization would wrap it a
 *  second time, producing a doubled `""search string""`. Exported for the
 *  widget's live-input commit paths (flushWord, commitValueStage) — anything
 *  that turns raw typed/pasted text into a stored chip value should run it
 *  through this first. A no-op for ordinary unquoted input. */
export function unquoteIfWrapped(s: string): string {
  if (s.length < 2 || s[0] !== '"' || s[s.length - 1] !== '"') return s;
  const inner = s.slice(1, -1);
  let out = '';
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i]!;
    if (c === '\\' && i + 1 < inner.length && (inner[i + 1] === '"' || inner[i + 1] === '\\')) {
      out += inner[i + 1];
      i += 1;
      continue;
    }
    out += c;
  }
  return out;
}

function serializeValue(operator: string, value: FilterValue): string {
  if (operator === 'range' && Array.isArray(value)) {
    return `${quote(value[0])}..${quote(value[1])}`;
  }
  const scalar = Array.isArray(value) ? value[0] : value;
  return quote(scalar);
}

function serializeNode(node: FilterNode): string {
  const bang = node.not ? '!' : '';
  switch (node.kind) {
    case 'cond': {
      // `*` right after the sigil = the any/all modifier (all bookings match).
      const star = node.scope === 'all' ? '*' : '';
      if (node.operator === 'range') {
        return `${bang}${node.namespace}=${star}${serializeValue('range', node.value)}`;
      }
      // gt/lt render as open-ended ranges: `ns=v..` / `ns=..v`.
      if (node.operator === 'gt' || node.operator === 'lt') {
        const v = serializeValue(node.operator, node.value);
        return node.operator === 'gt'
          ? `${bang}${node.namespace}=${star}${v}..`
          : `${bang}${node.namespace}=${star}..${v}`;
      }
      const sigil = node.operator === 'icontains' ? '=~' : node.operator === 'idmatch' ? '=#' : '=';
      return `${bang}${node.namespace}${sigil}${star}${serializeValue(node.operator, node.value)}`;
    }
    case 'freetext': {
      // Words within one freetext node stay space-adjacent; only SIBLING nodes
      // get &/| between them. Quote each word individually if needed.
      const words = node.value.split(' ').map(quote).join(' ');
      return `${bang}${words}`;
    }
    case 'group': {
      const sep = node.op === 'AND' ? ' & ' : ' | ';
      const inner = node.children.map(serializeNode).join(sep);
      return `${bang}(${inner})`;
    }
  }
}

/** Serialise the top-level array. Top-level siblings are implicit AND, so an
 *  explicit " & " separator is emitted between them. */
export function serialize(nodes: FilterNode[]): string {
  return nodes.map(serializeNode).join(' & ');
}
